import { useState, useRef, useEffect } from 'react';
import { Upload, Check, RotateCcw, Eye, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

/**
 * StyleStep — renders ONE field of options at a time.
 * `activeSection` is controlled by the parent. Auto-advance still happens on
 * selection (parent's `onActiveSectionChange` is called with the next field key).
 */
export default function StyleStep({
  designFields,
  selections,
  onChange,
  activeSection,
  onActiveSectionChange,
  onAllComplete,
}) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);

  // Fallback initial active if parent hasn't set one
  useEffect(() => {
    if (!activeSection && designFields[0]?.field_key) {
      onActiveSectionChange?.(designFields[0].field_key);
    }
  }, [activeSection, designFields, onActiveSectionChange]);

  const advanceTo = (nextKey) => {
    onActiveSectionChange?.(nextKey);
    // Smoothly scroll main content back to top
    setTimeout(() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' }), 80);
  };

  const handleSelect = (fieldKey, option) => {
    const updated = { ...selections, [fieldKey]: { type: 'preset', value: option.image_url, key: option.key, label: option.label } };
    onChange(updated);
    // Auto-advance
    const idx = designFields.findIndex((f) => f.field_key === fieldKey);
    if (idx < designFields.length - 1) {
      setTimeout(() => advanceTo(designFields[idx + 1].field_key), 400);
    } else if (Object.keys(updated).length === designFields.length) {
      onAllComplete?.();
    }
  };

  const handleUpload = async (fieldKey, file) => {
    setUploading(true);
    const formData = new FormData();
    formData.append('image', file);
    try {
      const res = await api.post('/upload/image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = res.data.data?.url || res.data.data?.image_url;
      if (url) {
        const updated = { ...selections, [fieldKey]: { type: 'upload', value: url, key: 'upload', label: 'Custom Upload' } };
        onChange(updated);
        const idx = designFields.findIndex((f) => f.field_key === fieldKey);
        if (idx < designFields.length - 1) {
          setTimeout(() => advanceTo(designFields[idx + 1].field_key), 500);
        }
      } else {
        toast.error('Upload succeeded but no URL was returned. Please try again.');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Upload failed';
      // eslint-disable-next-line no-console
      console.error('[upload/image] failed:', err?.response?.status, err?.response?.data || err);
      toast.error(`Upload failed: ${msg}`);
    }
    finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleReset = () => {
    onChange({});
    onActiveSectionChange?.(designFields[0]?.field_key || '');
  };

  const activeField = designFields.find((f) => f.field_key === activeSection);

  const sortOptions = (a, b) => {
    const parse = (opt) => {
      const url = opt?.image_url || '';
      const parts = url.split('/');
      const file = parts.pop() || '';
      const folder = parts.pop() || '';
      const m = file.match(/^(\d+)[_\-]/);
      const num = m ? parseInt(m[1], 10) : Number.MAX_SAFE_INTEGER;
      return { folder, num, label: opt?.label || '' };
    };
    const A = parse(a);
    const B = parse(b);
    if (A.folder !== B.folder) return A.folder.localeCompare(B.folder);
    if (A.num !== B.num) return A.num - B.num;
    return A.label.localeCompare(B.label);
  };

  const isTallSilhouette = (key) => /skirt|dress|gown|kurta_length|kurti_length|lehenga/i.test(key || '');
  const tileAspect = (key) => /^(front_neck|back_neck|sleeves?)(_|$)/i.test(key || '') ? 'aspect-square' : 'aspect-[3/4]';

  if (!activeField) return null;

  const idx = designFields.findIndex((f) => f.field_key === activeSection);
  const stepNum = idx + 1;
  const totalSteps = designFields.length;
  const prevField = designFields[idx - 1];
  const nextField = designFields[idx + 1];

  return (
    <div data-testid="style-step">
      {/* Field header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <div className="uppercase tracking-luxe text-[11px] font-semibold text-gold">
            Style · {stepNum} of {totalSteps}
          </div>
          {Object.keys(selections).length > 0 && (
            <button onClick={handleReset} className="uppercase tracking-luxe text-[11px] font-semibold text-ink-muted hover:text-destructive inline-flex items-center gap-1.5" data-testid="style-reset-btn">
              <RotateCcw className="w-3 h-3" /> Reset
            </button>
          )}
        </div>
        <h2 className="font-display font-light text-ink leading-none tracking-[-0.015em]" style={{ fontSize: 'clamp(30px, 4vw, 44px)' }} data-testid={`section-title-${activeField.field_key}`}>
          {activeField.field_label}
        </h2>
        <div className="flex items-center justify-between gap-4 mt-6 pb-5 border-b border-border">
          <div className="uppercase tracking-luxe text-[11px] font-semibold text-ink-muted">
            {activeField.options?.length || 0} Options
          </div>
          <div className="flex gap-6 text-[11px] uppercase tracking-luxe font-semibold">
            <button onClick={() => prevField && advanceTo(prevField.field_key)} disabled={!prevField} className="text-ink-muted hover:text-ink disabled:opacity-30 disabled:cursor-not-allowed" data-testid="field-prev">
              ← {prevField ? prevField.field_label : 'First'}
            </button>
            <button onClick={() => nextField && advanceTo(nextField.field_key)} disabled={!nextField} className="text-ink hover:text-gold disabled:opacity-30 disabled:cursor-not-allowed" data-testid="field-next">
              {nextField ? nextField.field_label : 'Last'} →
            </button>
          </div>
        </div>
      </div>

      {/* Options grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-testid={`section-${activeField.field_key}`}>
        {activeField.allow_upload && (
          <button
            onClick={() => fileRef.current?.click()}
            className={`${tileAspect(activeField.field_key)} bg-secondary relative flex flex-col items-center justify-center gap-2 transition-all ${
              selections[activeField.field_key]?.type === 'upload'
                ? 'outline outline-1 outline-offset-4 outline-ink'
                : 'border border-dashed border-ink/30 hover:border-ink'
            }`}
            data-testid={`upload-${activeField.field_key}`}
            disabled={uploading}
          >
            {selections[activeField.field_key]?.type === 'upload' && selections[activeField.field_key]?.value ? (
              <>
                <img src={selections[activeField.field_key].value.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${selections[activeField.field_key].value}` : selections[activeField.field_key].value} alt="Uploaded" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-ink text-background flex items-center justify-center"><Check className="w-3 h-3" /></div>
              </>
            ) : (
              <>
                <Upload className="w-6 h-6 text-ink-muted" strokeWidth={1.2} />
                <span className="uppercase tracking-luxe text-[10px] font-semibold text-ink-muted">{uploading ? 'Uploading…' : 'Upload Photo'}</span>
              </>
            )}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && handleUpload(activeField.field_key, e.target.files[0])} />
          </button>
        )}
        {[...(activeField.options || [])].sort(sortOptions).map((option) => {
          const sel = selections[activeField.field_key];
          const isSelected = sel?.type === 'preset' && sel?.key === option.key;
          const imgSrc = option.image_url?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${option.image_url}` : option.image_url;
          const fit = isTallSilhouette(activeField.field_key) ? 'object-contain bg-secondary p-2' : 'object-cover';
          return (
            <div key={option.key} className="group relative">
              <button
                onClick={() => handleSelect(activeField.field_key, option)}
                className={`block w-full ${tileAspect(activeField.field_key)} bg-secondary overflow-hidden relative transition-all ${isSelected ? 'outline outline-1 outline-offset-4 outline-ink' : ''}`}
                data-testid={`option-${option.key}`}
              >
                {imgSrc ? (
                  <img src={imgSrc} alt={option.label} className={`w-full h-full ${fit}`} loading="lazy" decoding="async" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center px-2">
                    <span className="text-[13px] text-ink-muted text-center">{option.label}</span>
                  </div>
                )}
                {isSelected && (
                  <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-ink text-background flex items-center justify-center pointer-events-none">
                    <Check className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </div>
                )}
              </button>
              {imgSrc && (
                <button
                  onClick={(e) => { e.stopPropagation(); setPreview({ url: imgSrc, label: option.label }); }}
                  className="absolute top-2 left-2 w-8 h-8 bg-background/90 hover:bg-background flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`preview-${option.key}`}
                  title="Preview"
                >
                  <Eye className="w-3.5 h-3.5 text-ink" strokeWidth={1.5} />
                </button>
              )}
              <div className="mt-2 text-center font-display text-ink" style={{ fontSize: 16 }}>{option.label}</div>
            </div>
          );
        })}
      </div>

      {/* Full-screen preview modal */}
      {preview && (
        <div className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4" onClick={() => setPreview(null)} data-testid="image-preview-modal">
          <button onClick={() => setPreview(null)} className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 flex items-center justify-center text-white" data-testid="image-preview-close">
            <X className="w-5 h-5" />
          </button>
          <div className="max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={preview.url.replace(/\.webp(\?.*)?$/, '_full.webp$1')} alt={preview.label} className="w-full max-h-[80vh] object-contain" loading="eager" decoding="async" />
            <p className="text-white text-center mt-4 font-display" style={{ fontSize: 22 }}>{preview.label}</p>
          </div>
        </div>
      )}
    </div>
  );
}
